import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Spinner } from '../../components/ui/icons';
import { fmtInt, progressOf } from '../../lib/format';
import { isRunActive } from '../../lib/runs';
import { invalidatePerformance, useChangelogRuns, useHistoryCoverage } from './queries';

/** Deal change history from Pipedrive: how much of the population is covered, load more, watch the run. */
export function HistoryPanel({
  pipelineIds,
  since,
  canRun,
}: {
  pipelineIds: number[];
  since: string;
  canRun: boolean;
}) {
  const { t } = useTranslation(['performance', 'common']);
  const coverage = useHistoryCoverage(pipelineIds, since);
  const runs = useChangelogRuns();
  const start = useMutation(
    trpc.pipedrive.changelog.startBackfill.mutationOptions({ onSuccess: invalidatePerformance }),
  );
  const active = runs.data?.find((r) => isRunActive(r.status));
  const last = runs.data?.[0];
  const progress = progressOf(active?.stats?.progress);
  const lastStats = (last?.stats ?? null) as { changes?: number; processed?: number } | null;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('history.title')} subtitle={t('history.subtitle')} icon="history">
        {active ? (
          <Spinner>
            {t('history.inProgress', { processed: fmtInt(progress?.processed ?? 0), of: progress?.of ?? '?' })}
          </Spinner>
        ) : (
          canRun && (
            <HpButton
              variant="secondary"
              icon="download"
              className="bo-button--sm"
              busy={start.isPending}
              disabled={pipelineIds.length === 0}
              onClick={() => start.mutate({ pipelineIds, since, refetch: false })}
            >
              {t('history.load', { since })}
            </HpButton>
          )
        )}
      </HpCardHeader>
      <div className="hp-card__body bo-sections">
        <ErrorAlert error={start.error ?? coverage.error ?? runs.error} />
        {coverage.data && (
          <p>
            {t('history.coverage', {
              withHistory: fmtInt(coverage.data.withHistory),
              deals: fmtInt(coverage.data.deals),
              changes: fmtInt(coverage.data.changes),
              since,
            })}
          </p>
        )}
        {last && !active ? (
          <p className="bo-row bo-muted">
            <span>{t('history.lastRun', { id: last.id })}</span>
            <StatusBadge status={last.status} />
            <span>
              {t('history.lastRunStats', {
                changes: fmtInt(lastStats?.changes ?? 0),
                processed: fmtInt(lastStats?.processed ?? 0),
              })}
            </span>
            <RelativeTime value={last.finishedAt ?? last.createdAt} />
            {last.status === 'failed' && last.error && <span className="bo-clip">{last.error}</span>}
          </p>
        ) : (
          !active && <p className="bo-muted">{t('history.noRuns')}</p>
        )}
        <p className="bo-hint">{t('history.auto')}</p>
      </div>
    </section>
  );
}
